import React, { useMemo } from "react"
import { CollectionFunnelData } from "../connect"
import FunnelChart, {
  FunnelStage,
} from "~/ui-rtk/components/charts/FunnelChart"
import { Loader } from "~/ui-rtk/components/ui/common"
import BenchmarkStatCard from "~/ui-rtk/components/ui/charts/BenchmarkStatCard"
import { FunnelBenchmarks, type PlottedRow } from "../../types"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

export interface ConversionFlowStep {
  label: string
  value: number
  conversionRate?: number
  abandonmentRate?: number
  lostCount?: number
}

interface CollectionFunnelChartProps {
  data: CollectionFunnelData | null
  compareData?: CollectionFunnelData | null
  conversionFlowSteps?: ConversionFlowStep[]
  showSummaryStats?: boolean
  showConversionFlow?: boolean
  isLoading?: boolean
  benchmarks?: FunnelBenchmarks | null
  showBenchmarks?: boolean
  plottedRows?: PlottedRow[]
}

export default function CollectionFunnelChart({
  data,
  compareData,
  conversionFlowSteps,
  showSummaryStats = true,
  showConversionFlow = true,
  isLoading = false,
  benchmarks,
  showBenchmarks = false,
  plottedRows = [],
}: CollectionFunnelChartProps) {
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const funnelStages = useMemo<FunnelStage[]>(() => {
    if (!data) return []

    return [
      {
        label: "Collection Views",
        value: data.collectionViewers,
        percentage: 100,
        abandonmentCount: 0,
        abandonmentRate: 0,
        avgOrderValue: data.avgOrderValue,
      },
      {
        label: "Add to Cart",
        value: data.addersToCart,
        percentage: data.collectionToCartConversion,
        abandonmentCount: data.collectionViewers - data.addersToCart,
        abandonmentRate: data.collectionToCartAbandon,
        revenue: data.revenueAtCart,
        avgOrderValue: data.avgOrderValue,
        firstOrders:
          data.purchasers === 0
            ? 0
            : Math.round(
                data.firstOrders * (data.addersToCart / data.purchasers),
              ),
        repeatOrders:
          data.purchasers === 0
            ? 0
            : Math.round(
                data.repeatOrders * (data.addersToCart / data.purchasers),
              ),
      },
      {
        label: "Purchases",
        value: data.purchasers,
        percentage: data.collectionToPurchaseConversion,
        abandonmentCount: data.addersToCart - data.purchasers,
        abandonmentRate: data.cartToPurchaseAbandon,
        revenue: data.revenueAtPurchase,
        avgOrderValue: data.avgOrderValue,
        firstOrders: data.firstOrders,
        repeatOrders: data.repeatOrders,
      },
    ]
  }, [data])

  const compareFunnelStages = useMemo<FunnelStage[] | undefined>(() => {
    if (!compareData) return undefined

    return [
      {
        label: "Collection Views",
        value: compareData.collectionViewers,
        percentage: 100,
        abandonmentCount: 0,
        abandonmentRate: 0,
        avgOrderValue: compareData.avgOrderValue,
      },
      {
        label: "Add to Cart",
        value: compareData.addersToCart,
        percentage: compareData.collectionToCartConversion,
        abandonmentCount:
          compareData.collectionViewers - compareData.addersToCart,
        abandonmentRate: compareData.collectionToCartAbandon,
        revenue: compareData.revenueAtCart,
        avgOrderValue: compareData.avgOrderValue,
        firstOrders:
          compareData.purchasers === 0
            ? 0
            : Math.round(
                compareData.firstOrders *
                  (compareData.addersToCart / compareData.purchasers),
              ),
        repeatOrders:
          compareData.purchasers === 0
            ? 0
            : Math.round(
                compareData.repeatOrders *
                  (compareData.addersToCart / compareData.purchasers),
              ),
      },
      {
        label: "Purchases",
        value: compareData.purchasers,
        percentage: compareData.collectionToPurchaseConversion,
        abandonmentCount: compareData.addersToCart - compareData.purchasers,
        abandonmentRate: compareData.cartToPurchaseAbandon,
        revenue: compareData.revenueAtPurchase,
        avgOrderValue: compareData.avgOrderValue,
        firstOrders: compareData.firstOrders,
        repeatOrders: compareData.repeatOrders,
      },
    ]
  }, [compareData])

  // Default conversion flow steps if not provided
  const defaultConversionFlowSteps = useMemo<ConversionFlowStep[]>(() => {
    if (!data) return []

    return [
      {
        label: "Collection Views",
        value: data.collectionViewers,
      },
      {
        label: "Add to Cart",
        value: data.addersToCart,
        conversionRate: data.collectionToCartConversion,
        abandonmentRate: data.collectionToCartAbandon,
        lostCount: data.collectionViewers - data.addersToCart,
      },
      {
        label: "Purchases",
        value: data.purchasers,
        conversionRate: data.cartToPurchaseConversion,
        abandonmentRate: data.cartToPurchaseAbandon,
        lostCount: data.addersToCart - data.purchasers,
      },
    ]
  }, [data])

  // Compute plotted-row-based conversion rates when rows are plotted
  // Collection stages: [0] Collection Views, [1] Add to Cart, [2] Purchases
  const plottedStats = useMemo(() => {
    if (plottedRows.length === 0) return null
    const sumViews = plottedRows.reduce(
      (s, r) => s + (r.stages[0]?.value ?? 0),
      0,
    )
    const sumCart = plottedRows.reduce(
      (s, r) => s + (r.stages[1]?.value ?? 0),
      0,
    )
    const sumPurchases = plottedRows.reduce(
      (s, r) => s + (r.stages[2]?.value ?? 0),
      0,
    )
    return {
      collectionToCart: sumViews > 0 ? (sumCart / sumViews) * 100 : 0,
      cartToPurchase: sumCart > 0 ? (sumPurchases / sumCart) * 100 : 0,
      collectionToPurchase: sumViews > 0 ? (sumPurchases / sumViews) * 100 : 0,
    }
  }, [plottedRows])

  const flowStepsToRender = conversionFlowSteps || defaultConversionFlowSteps

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size={50} />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-96 text-system-gray-400">
        No data available for the selected time period.
      </div>
    )
  }

  const hasData =
    data.collectionViewers > 0 || data.addersToCart > 0 || data.purchasers > 0

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-system-gray-400 text-lg mb-2">
            No funnel data available
          </p>
          <p className="text-system-gray-500 text-sm">
            Try adjusting your date range or filters
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Summary Stats with Benchmark Bars */}
      {showSummaryStats && data && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <BenchmarkStatCard
            label="Collection → Cart"
            currentValue={
              plottedStats?.collectionToCart ?? data.collectionToCartConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.collectionToCart
                : data.collectionToCartAbandon
            }
            benchmark={benchmarks?.collection_to_cart}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
          <BenchmarkStatCard
            label="Cart → Purchase"
            currentValue={
              plottedStats?.cartToPurchase ?? data.cartToPurchaseConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.cartToPurchase
                : data.cartToPurchaseAbandon
            }
            benchmark={benchmarks?.cart_to_purchase}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
          <BenchmarkStatCard
            label="Collection → Purchase"
            currentValue={
              plottedStats?.collectionToPurchase ??
              data.collectionToPurchaseConversion
            }
            abandonRate={
              plottedStats
                ? 100 - plottedStats.collectionToPurchase
                : data.collectionToPurchaseAbandon
            }
            benchmark={benchmarks?.collection_to_purchase}
            showBenchmark={showBenchmarks && !plottedStats}
            valueColor="green"
          />
        </div>
      )}

      {/* Reusable Funnel Chart */}
      <FunnelChart
        stages={funnelStages}
        compareStages={compareFunnelStages}
        plottedRows={plottedRows}
      />

      {/* Legend for plotted rows */}
      {plottedRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-6 mt-4 px-2">
          {plottedRows.map((row, idx) => (
            <div
              key={row.id || `legend-${idx}`}
              className="flex items-center gap-2"
            >
              <div
                className="w-8 h-2 rounded"
                style={{ backgroundColor: row.color }}
              />
              <span
                className={cn(
                  "text-sm text-system-gray-400",
                  isDemoEnabled && "blur-md",
                )}
              >
                {row.label}
              </span>
            </div>
          ))}
          {compareFunnelStages && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 rounded border-t-2 border-dashed border-white" />
              <span className="text-sm text-system-gray-400">
                Comparison Period
              </span>
            </div>
          )}
        </div>
      )}

      {/* Conversion Flow Summary */}
      {showConversionFlow && flowStepsToRender.length > 0 && (
        <div className="flex items-center justify-around mt-6 text-sm">
          {flowStepsToRender.map((step, index) => {
            const nextStep = flowStepsToRender[index + 1]
            return (
              <React.Fragment key={`flow-step-${index}`}>
                {/* Step */}
                <div className="text-center flex-1">
                  <div className="font-semibold text-white text-lg">
                    {step.value.toLocaleString()}
                  </div>
                  <div className="text-system-gray-400">{step.label}</div>
                </div>

                {/* Arrow between steps */}
                {index < flowStepsToRender.length - 1 && nextStep && (
                  <div className="text-center px-4">
                    <div className="text-white text-xl">→</div>
                    {nextStep.abandonmentRate !== undefined && (
                      <div className="text-xs text-green-400 font-semibold mt-1">
                        {(100 - nextStep.abandonmentRate).toFixed(1)}%
                        conversion
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}
